import { CATALOG, type Part } from './catalog';
import { BUSBAR_FROM, capCurrent, earthLeakage, feederSection, parseSteps, type IncomerKind } from './distribution';
import { busbarSize } from './layout';
import { onSeg, pinsW } from './geometry';
import { LIB } from './library';
import { pickPart, ratedOf, type GenResult, type PartWant } from './generate';
import { t as $t } from './i18n';
import { newId } from './tagging';
import type { Page, Project, Pt, PType, Sym, SymProps } from './types';

/*
 * Power boards with several busbar sections: every section has its own sources
 * (transformer, generator, a line from a synchronising board), outgoing feeders
 * and optional power factor correction; neighbouring sections are joined by a
 * bus coupler. Sources of one section are either interlocked (one at a time)
 * or synchronised (in parallel, as on a generator synchronising board).
 *
 * Each section's busbars run from page to page by break points named L1-A,
 * L2-A, L3-A, N-A for section A (L1, L2, L3, N with a single section); the PE
 * busbar is common to the whole board.
 */

export type SourceKind = 'tr' | 'gen' | 'line';
export interface SourceSpec {
  kind: SourceKind;
  /** name on the drawings, e.g. "TR1", "G2", "SEN-1" */
  name: string;
  /** rated current of its breaker in A */
  amps: number;
  /** breaker type */
  sw: IncomerKind;
  /** the neutral is switched (4-pole breaker) */
  poles4?: boolean;
  /** current transformers and an energy analyser */
  meter?: boolean;
  /** free text shown at the supply, e.g. "1000 kVA 22/0,4 kV" */
  note?: string;
}
export interface BoardFeeder {
  tag?: string;
  desc: string;
  amps: number;
  /** 1 or 3 phases; 4 = three phases with switched neutral */
  poles: 1 | 3 | 4;
  rcd?: boolean;
  cableLen?: number;
  /** busbar trunking instead of a cable (no terminals) */
  busway?: boolean;
}
export interface SectionSpec {
  /** "A", "B", … (filled in when empty) */
  name?: string;
  sources: SourceSpec[];
  feeders: BoardFeeder[];
  /** capacitor steps in kvar for this section */
  comp?: number[];
  spd?: boolean;
}
export interface BoardSpec {
  title?: string;
  sections: SectionSpec[];
  /** couplers between section i and i + 1 (one fewer than sections) */
  couplers?: { amps: number; sw: IncomerKind; poles4?: boolean }[];
  /** sources of a section run in parallel (synchronising board) instead of one at a time */
  sync?: boolean;
  /** neutral busbar sized for half the phase current (default: the same as the phases, 1:1) */
  nHalf?: boolean;
}

const SW_TYPE: Record<IncomerKind, string> = { lbs: 'lbs3', mccb: 'mccb3', acb: 'cb3m' };
const X = (k: number) => 32 + 40 * k;
const COLS = 9;
const MCB_MAX = 63, DIRECT_FROM = 160;
/** largest moulded case breaker of the built-in series; above it feeders get an air circuit breaker */
const MCCB_MAX = 1600;
export const MAX_BOARD_FEEDER = 4000;

/** Copper cross-section of a feeder cable, with as many parallel 240 mm² runs as the current needs above 2 × 240. */
export function feederCable(amps: number): string {
  const one = feederSection(amps);
  if (amps <= 2 * 364) return one;
  return `${Math.ceil(amps / 364)}×240`;
}

/** Section letter for index i (A, B, …). */
export const sectionName = (i: number) => String.fromCharCode(65 + i);

/** Busbar names of a section: "L1-A" … with several sections, plain "L1" … with one; PE is common. */
export function busNames(spec: BoardSpec, i: number) {
  const sfx = spec.sections.length > 1 ? `-${spec.sections[i].name || sectionName(i)}` : '';
  return { L1: 'L1' + sfx, L2: 'L2' + sfx, L3: 'L3' + sfx, N: 'N' + sfx, PE: 'PE' };
}

/** Rated current of a section: its largest source (or the coupler feeding it). */
export function sectionAmps(spec: BoardSpec, i: number) {
  const s = spec.sections[i];
  const cpl = [spec.couplers?.[i - 1]?.amps ?? 0, spec.couplers?.[i]?.amps ?? 0];
  return Math.max(0, ...s.sources.map((x) => x.amps), ...cpl);
}

/** Checks a board description; returns the problems in the user's words. */
export function checkBoard(spec: BoardSpec): string[] {
  const out: string[] = [];
  if (!spec.sections.length) out.push($t('Panoda en az bir bara bölümü olmalı.'));
  if ((spec.couplers?.length ?? 0) !== Math.max(0, spec.sections.length - 1)) out.push($t('Bölümler arasında kuplaj sayısı bölüm sayısının bir eksiği olmalı.'));
  spec.sections.forEach((s, i) => {
    const n = s.name || sectionName(i);
    if (!s.sources.length && !(spec.couplers?.[i - 1] || spec.couplers?.[i])) out.push($t('Bölüm {0}: kaynak (giriş) ya da kuplaj yok.', n));
    for (const src of s.sources) if (!(src.amps >= 16 && src.amps <= 6300)) out.push($t('{0}: giriş akımı 16–6300 A olmalı.', src.name));
    for (const f of s.feeders) if (!(f.amps >= 1 && f.amps <= MAX_BOARD_FEEDER)) out.push($t('{0}: çıkış akımı 1–{1} A olmalı.', f.desc || f.tag || '?', MAX_BOARD_FEEDER));
  });
  return out;
}

/**
 * Draws a board with busbar sections: one page per source, one per coupler, the feeders and power factor
 * correction of each section, the interlocking of motorised breakers and a single-line overview.
 */
/**
 * Terminal numbers of known controllers, as wired in delivered projects (DEIF AGC 150: Lujo Otel synchronising
 * board; Woodward easYgen-2300: İZTO): the AC measuring terminals in gctl pin order, and per unit kind the digital inputs (breaker closed,
 * breaker open, emergency stop, reset; generators also engine fault and protection breaker) and outputs (start,
 * close, open, running, alarm), and a generator's speed and voltage bias outputs (GOV +/−, AVR +/−). Other models are numbered
 * generically (DI1, DO1 …).
 */
const CTL_TERMINALS: { match: RegExp; ac: string; gen: { di: string[]; dq: string[]; ao: string[] }; mains?: { di: string[]; dq: string[] } }[] = [
  {
    match: /agc\s*-?\s*150/i,
    ac: '56,57,58,59,63,64,65,62,67,68,69,66,1,2,30,32,33,35',
    gen: { di: ['49', '50', '4', '41', '44', '43'], dq: ['5', '17', '18', '10', '12'], ao: ['52', '51', '55', '54'] },
    mains: { di: ['47', '48', '4', '41'], dq: ['15', '16', '10', '12'] },
  },
  {
    // Woodward easYgen-2300 / 2500 as wired at İZTO (generator units only; '' = not in that drawing, numbered generically)
    match: /easy\s*gen\s*-?\s*2[35]00/i,
    ac: '5,6,7,4,15,17,19,21,23,25,27,29,53,54,59,58,57,56',
    gen: { di: ['49', '51', '44', '50', '46', '48'], dq: ['32/33', '41/42', '', '', '30/31'], ao: ['1', '3', '8', '9'] },
  },
];

export interface BoardControl {
  /** controller model written on the drawings, e.g. "DEIF AGC 150", "Woodward easYgen-3000" */
  model: string;
}

export function generateBoard(pr: Project, spec: BoardSpec, opt: { parts?: Part[]; fallback?: Part[]; singleLine?: boolean; control?: BoardControl } = {}): GenResult {
  const ctl = opt.control;
  /** sources with a controller: their breaker tag and section, for the control pages */
  const units: { name: string; kind: SourceKind | 'cpl'; Q: string; si: number; page: string }[] = [];
  const parts = opt.parts ?? CATALOG;
  // the preferred parts (one maker) first, every other part where they have nothing suitable
  const pick = (type: string, w: PartWant) => pickPart(parts, type, w) ?? (opt.fallback ? pickPart(opt.fallback, type, w) : undefined);
  const warnings: string[] = [];
  const pages: Page[] = [];
  spec = { ...spec, sections: spec.sections.map((s, i) => ({ ...s, name: s.name || sectionName(i) })) };
  const has = (pn: string) => (parts.some((p) => p.pn === pn) || opt.fallback?.some((p) => p.pn === pn) ? pn : '');

  // tag numbering as in the other generators: single-line helper symbols do not reserve their tags
  const real = pr.pages.flatMap((p) => p.syms.filter((s) => !['sl_cb', 'sl_rcd', 'sl_k', 'sl_fsd', 'sl_kwh'].includes(s.type)));
  const used = new Map<string, number>();
  for (const s of real) {
    const m = /^([A-Z]+)(\d+)$/.exec(s.tag || '');
    if (m) used.set(m[1], Math.max(used.get(m[1]) ?? 0, +m[2]));
  }
  const taken = new Set(real.map((s) => s.tag));
  const tag = (prefix: string) => {
    let n = (used.get(prefix) ?? 0) + 1;
    while (taken.has(prefix + n)) n++;
    used.set(prefix, n);
    taken.add(prefix + n);
    return prefix + n;
  };
  for (const s of spec.sections) for (const f of s.feeders) if (f.tag) taken.add(f.tag);
  // feeder terminals: one strip per busbar section (X1 for A, X2 for B …), numbered on from what the project has
  const terms = new Map<string, number>();
  for (const pg of pr.pages) for (const s of pg.syms) if (s.type === 'term' && /^X\d+$/.test(s.tag)) terms.set(s.tag, Math.max(terms.get(s.tag) ?? 0, parseInt(s.props.pinNo || '0', 10) || 0));
  const termNo = (strip: string) => {
    const n = (terms.get(strip) ?? 0) + 1;
    terms.set(strip, n);
    return String(n);
  };

  const page = (desc: string) => {
    const p: Page = { id: newId(), desc, syms: [], wires: [] };
    pages.push(p);
    return p;
  };
  const S = (p: Page, type: string, x: number, y: number, o: { rot?: number; tag?: string; desc?: string; part?: string; props?: SymProps } = {}) => {
    const s: Sym = { id: newId(), type, x, y, rot: o.rot || 0, tag: o.tag || '', desc: o.desc || '', part: o.part || '', maker: '', props: o.props || {} };
    if (s.part) s.maker = (parts.find((q) => q.pn === s.part) ?? opt.fallback?.find((q) => q.pn === s.part))?.maker ?? '';
    p.syms.push(s);
    return s;
  };
  const W = (p: Page, pts: Pt[], cs = '', col = '') => p.wires.push({ id: newId(), pts, cs, col });
  const choose = (who: string, what: string, type: string, w: PartWant, poles4 = false) => {
    let p = poles4 ? pick(type, { ...w, poles: 4 }) : undefined;
    if (poles4 && !p) {
      p = pick(type, w);
      if (p) warnings.push($t('{0}: 4 kutuplu {1} bulunamadı, 3 kutuplu seçildi; nötr kesmeli parçayı elle seçin.', who, what));
    }
    if (!p) p = pick(type, w);
    if (!p) warnings.push($t('{0}: {1} için uygun parça bulunamadı{2}', who, what, w.rated != null ? ` (≥ ${w.rated} A)` : ''));
    return p?.pn ?? '';
  };
  const termPart = (sec: string, pe: boolean) => {
    const c = Number(sec);
    if (!Number.isFinite(c)) return '';
    const fit = [2.5, 4, 6, 10, 16, 35, 70, 120, 240].find((x) => x >= c);
    if (fit == null) return '';
    if (pe) return has(fit <= 4 ? 'KL-4-PE' : `KL-${fit}-PE`) || has(`KL-${fit}`);
    return has(fit === 2.5 ? 'KL-2.5' : `KL-${fit}`);
  };
  const wireCs = (a: number) => (feederCable(a).includes('×') ? '' : feederCable(a));
  /** busbar rows at the top of feeder and correction pages: [conductor, y, type, colour] */
  const ROWS = (b: ReturnType<typeof busNames>): [string, number, PType, string][] => [[b.L1, 24, 'L', 'BK'], [b.L2, 32, 'L', 'BK'], [b.L3, 40, 'L', 'BK'], [b.N, 48, 'N', 'BU'], [b.PE, 56, 'PE', 'GNYE']];
  /** busbar outlets at the bottom right of source and coupler pages: [conductor, y] */
  const OUT = (b: ReturnType<typeof busNames>): [string, number, PType, string][] => [[b.L1, 160, 'L', 'BK'], [b.L2, 176, 'L', 'BK'], [b.L3, 192, 'L', 'BK'], [b.N, 200, 'N', 'BU'], [b.PE, 208, 'PE', 'GNYE']];

  /** breakers that need an interlock: tag and what closes the bus with them */
  const brk: { tag: string; sec: number; role: 'src' | 'cpl'; acb: boolean }[] = [];
  const busPages = new Map<string, number>(); // page id → section amps (for copper busbar marking)
  const group = new Map<string, string>(); // page id → compartment group: src0, fdr0, cpl0 …

  /**
   * To a controller from a power page: the current transformer secondaries (S1 each, the S2s joined to one common
   * return) and, through fuses, the voltage of the incoming rows at the top left (y 24…48).
   */
  const toController = (p: Page, n: string, cts: { x: number; y: number }[]) => {
    cts.forEach(({ x, y }, i) => {
      W(p, [[x + 4, y - 8], [x + 4, 112]], '2.5', 'BK');
      S(p, 'brk', x + 4, 112, { rot: 2, props: { name: `${n}.I${i + 1}`, ptype: '' } });
      W(p, [[x + 12, y - 8], [x + 12, 128]], '2.5', 'BK');
    });
    if (cts.length) {
      const x0 = cts[0].x + 12, x1 = cts[cts.length - 1].x + 12;
      W(p, [[x0, 128], [x1, 128], [x1 + 12, 128], [x1 + 12, 112]], '2.5', 'BK');
      S(p, 'brk', x1 + 12, 112, { rot: 2, props: { name: `${n}.IC`, ptype: '' } });
    }
    [0, 1, 2, 3].forEach((i) => {
      const x = 44 + 16 * i, y = 24 + 8 * i;
      if (i < 3) {
        W(p, [[x, y], [x, 72]], '1', 'BK');
        S(p, 'fuse', x, 72, { tag: tag('F'), part: has('SG-2A-5x20') });
        W(p, [[x, 88], [x, 100]], '1', 'BK');
      } else W(p, [[x, y], [x, 100]], '1', 'BU');
      S(p, 'brk', x, 100, { props: { name: `${n}.UG${i < 3 ? i + 1 : 'N'}`, ptype: i < 3 ? 'L' : 'N' } });
    });
  };

  /* ---- sources: one page each, supply from the left, breaker, current transformers, onto the section busbars ---- */
  spec.sections.forEach((sec, si) => {
    const b = busNames(spec, si), amps = sectionAmps(spec, si);
    sec.sources.forEach((src) => {
      const p = page($t('{0} girişi · bölüm {1} · {2} A', src.name, sec.name!, src.amps));
      group.set(p.id, `src${si}`);
      busPages.set(p.id, amps);
      const cs = wireCs(src.amps), a = X(2);
      const what = src.kind === 'tr' ? $t('Trafo') : src.kind === 'gen' ? $t('Jeneratör') : $t('Hat');
      S(p, 'text', 34, 218, { props: { text: `${what} ${src.name}${src.note ? ' · ' + src.note : ''}`, size: 3 } });
      S(p, 'text', 34, 224, { props: { text: '3/N/PE ~ 400/230 V 50 Hz', size: 2.5 } });
      const Q = tag('Q');
      const type = SW_TYPE[src.sw];
      S(p, type, a, 64, {
        tag: Q,
        desc: `${src.name} ${$t('giriş şalteri')}${src.poles4 ? ' · 4P' : ''}`,
        part: choose(Q, $t('giriş şalteri'), type, { rated: src.amps }, !!src.poles4),
      });
      brk.push({ tag: Q, sec: si, role: 'src', acb: src.sw === 'acb' });
      if (ctl) units.push({ name: src.name, kind: src.kind, Q, si, page: p.id });
      // with a controller the current transformers feed it, without one the energy analyser
      const metered = !!(src.meter || ctl);
      const ctPart = metered ? choose(Q, $t('akım trafosu'), 'ct', { rated: src.amps }) : '';
      const OUTS = OUT(b);
      const cts: { x: number; y: number }[] = [];
      OUTS.forEach(([name, yOut, pt, col], i) => {
        const x = a + 8 * i, yIn = 24 + 8 * i;
        // the supply phases are the network's L1 L2 L3 N PE; which source it is reads from the text and the breaker
        S(p, 'pot', 28, yIn, { props: { name: ['L1', 'L2', 'L3', 'N', 'PE'][i], ptype: pt } });
        S(p, 'brk', 400, yOut, { rot: 3, props: { name, ptype: pt } });
        if (i < 3) {
          W(p, [[28, yIn], [x, yIn], [x, 64]], cs, col);
          if (metered) {
            const cx = X(4) + 24 * i;
            W(p, [[x, 88], [x, yOut], [cx, yOut]], cs, col);
            S(p, 'ct', cx, yOut, { rot: 3, tag: tag('T'), desc: $t('{0} akım trafosu', ['L1', 'L2', 'L3'][i]), part: ctPart });
            W(p, [[cx + 16, yOut], [400, yOut]], cs, col);
            cts.push({ x: cx, y: yOut });
          } else W(p, [[x, 88], [x, yOut], [400, yOut]], cs, col);
        } else W(p, [[28, yIn], [x, yIn], [x, yOut], [400, yOut]], cs, col);
      });
      if (ctl) toController(p, src.name, cts);
      else if (src.meter) {
        const ax = X(4), ay = 100;
        S(p, 'emeter', ax, ay, { tag: tag('P'), desc: $t('{0} enerji analizörü', src.name), part: has('EA-3F') });
        cts.forEach(({ x, y }) => {
          W(p, [[x + 4, y - 8], [x + 4, ay + 32]], '2.5', 'BK');
          W(p, [[x + 12, y - 8], [x + 12, ay + 32]], '2.5', 'BK');
        });
        OUTS.slice(0, 4).forEach(([, yOut], i) => {
          const x = ax + 68 + 8 * i;
          if (i < 3) {
            W(p, [[x, yOut], [x, 152]], '1', 'BK');
            S(p, 'fuse', x, 136, { tag: tag('F'), part: has('SG-2A-5x20') });
            W(p, [[x, 136], [x, ay + 32]], '1', 'BK');
          } else W(p, [[x, yOut], [x, ay + 32]], '1', 'BU');
        });
      }
    });
  });

  /* ---- bus couplers: section i busbars from the left, coupler breaker, section i + 1 busbars to the right ---- */
  (spec.couplers ?? []).forEach((c, ci) => {
    if (ci >= spec.sections.length - 1) return;
    const A = busNames(spec, ci), B = busNames(spec, ci + 1), na = spec.sections[ci].name!, nb = spec.sections[ci + 1].name!;
    const p = page($t('Kuplaj · bölüm {0} – {1} · {2} A', na, nb, c.amps));
    group.set(p.id, `cpl${ci}`);
    busPages.set(p.id, Math.max(sectionAmps(spec, ci), sectionAmps(spec, ci + 1)));
    const Q = tag('Q'), a = X(3), cs = wireCs(c.amps), type = SW_TYPE[c.sw];
    S(p, type, a, 64, { tag: Q, desc: $t('Kuplaj {0}–{1}', na, nb) + (c.poles4 ? ' · 4P' : ''), part: choose(Q, $t('kuplaj şalteri'), type, { rated: c.amps }, !!c.poles4) });
    brk.push({ tag: Q, sec: ci, role: 'cpl', acb: c.sw === 'acb' });
    const ins = ROWS(A).slice(0, 4), outs = OUT(B).slice(0, 4);
    // with controllers the coupler has its own: current transformers on the B side, bus A voltage, bus B on its page
    const cn = `KUP-${na}${nb}`, cts: { x: number; y: number }[] = [];
    if (ctl) units.push({ name: cn, kind: 'cpl', Q, si: ci + 1, page: p.id });
    const ctPart = ctl ? choose(Q, $t('akım trafosu'), 'ct', { rated: c.amps }) : '';
    ins.forEach(([name, y, pt, col], i) => {
      const x = a + 8 * i;
      S(p, 'brk', 28, y, { rot: 1, props: { name, ptype: pt } });
      S(p, 'brk', 400, outs[i][1], { rot: 3, props: { name: outs[i][0], ptype: pt } });
      if (i < 3 && ctl) {
        const yo = outs[i][1], cx = X(5) + 24 * i;
        W(p, [[28, y], [x, y], [x, 64]], cs, col);
        W(p, [[x, 88], [x, yo], [cx, yo]], cs, col);
        S(p, 'ct', cx, yo, { rot: 3, tag: tag('T'), desc: $t('{0} akım trafosu', ['L1', 'L2', 'L3'][i]), part: ctPart });
        W(p, [[cx + 16, yo], [400, yo]], cs, col);
        cts.push({ x: cx, y: yo });
      } else if (i < 3) {
        W(p, [[28, y], [x, y], [x, 64]], cs, col);
        W(p, [[x, 88], [x, outs[i][1]], [400, outs[i][1]]], cs, col);
      } else if (c.poles4) {
        // switched neutral: through the coupler's fourth pole (drawn as a separate path)
        W(p, [[28, y], [x, y], [x, outs[i][1]], [400, outs[i][1]]], cs, col);
      } else W(p, [[28, y], [x, y], [x, outs[i][1]], [400, outs[i][1]]], cs, col);
    });
    if (ctl) toController(p, cn, cts);
    S(p, 'text', 300, 228, { props: { text: $t('PE barası tüm bölümlerde ortak'), size: 2.2 } });
  });

  /* ---- feeders of each section ---- */
  const slFeeders: { sec: number; t: string; f: BoardFeeder; rcd: string }[] = [];
  spec.sections.forEach((sec, si) => {
    const b = busNames(spec, si), rows = ROWS(b);
    let fp: Page | null = null, col = COLS + 1, first = '', last = '', single = 0;
    const flush = () => {
      if (fp) fp.desc = $t('Çıkışlar · bölüm {0} · {1}', sec.name!, first === last ? first : `${first}–${last}`);
    };
    sec.feeders.forEach((f) => {
      const three = f.poles !== 1;
      // earth leakage protection above the RCD range: core balance CT, relay and shunt trip, one column more
      const toroid = !!f.rcd && three && f.amps > MCB_MAX;
      const w = three ? (toroid ? 3 : 2) : 1;
      if (!fp || col + w - 1 > COLS || (w === 1 && col === COLS)) {
        flush();
        fp = page('');
        group.set(fp.id, `fdr${si}`);
        busPages.set(fp.id, sectionAmps(spec, si));
        rows.forEach(([n, y, t, c]) => {
          S(fp!, 'brk', 28, y, { rot: 1, props: { name: n, ptype: t } });
          W(fp!, [[28, y], [400, y]], '', c);
          S(fp!, 'brk', 400, y, { rot: 3, props: { name: n, ptype: t } });
        });
        col = 1;
        first = '';
      }
      const pg = fp, x = X(col), fcs = wireCs(f.amps);
      const type = !three ? 'cb1' : f.amps <= MCB_MAX ? 'cb3n' : f.amps <= MCCB_MAX ? 'mccb3' : 'cb3m';
      const t = f.tag || tag(f.amps <= MCB_MAX ? 'F' : 'Q');
      if (!first) first = t;
      last = t;
      const ph = !three ? rows[single++ % 3] : null;
      const phases = three ? [x, x + 8, x + 16] : [x];
      const nX = x + 8 * phases.length, peX = nX + 8;
      const drop = pg.wires.length;
      if (ph) W(pg, [[x, ph[1]], [x, 64]], fcs, 'BK');
      else phases.forEach((px, k) => W(pg, [[px, 24 + 8 * k], [px, 64]], fcs, 'BK'));
      // big feeders hang on the busbars by copper (droppers), as the incomers do
      if (f.amps >= BUSBAR_FROM) for (const w of pg.wires.slice(drop)) w.bus = busbarSize(f.amps);
      S(pg, type, x, 64, {
        tag: t,
        desc: (ph ? `${f.desc} · ${ph[0]}` : f.desc) + (f.poles === 4 ? ' · 4P' : ''),
        part: choose(t, $t('şalter'), type, { rated: f.amps }, f.poles === 4),
      });
      let rcd = !!f.rcd, rcdTag = '';
      if (rcd && f.amps > MCB_MAX) rcd = false;
      if (toroid) rcdTag = earthLeakage({ S, W, tag, has, choose, warnings }, pg, x, nX, t, f.amps);
      let yN = 160;
      if (rcd) {
        phases.forEach((px) => W(pg, [[px, 88], [px, 100]], fcs, 'BK'));
        const rType = three ? 'rcd4' : 'rcd2';
        rcdTag = tag('F');
        S(pg, rType, x, 100, { tag: rcdTag, desc: $t('{0} kaçak akım', t), part: choose(rcdTag, $t('kaçak akım rölesi'), rType, { rated: f.amps, text: f.amps <= 40 ? '30 mA' : '300 mA' }) });
        W(pg, [[nX, 48], [nX, 100]], fcs, 'BU');
        phases.forEach((px) => W(pg, [[px, 124], [px, 160]], fcs, 'BK'));
        yN = 124;
      } else phases.forEach((px) => W(pg, [[px, 88], [px, 160]], fcs, 'BK'));
      W(pg, rcd ? [[nX, yN], [nX, 160]] : [[nX, 48], [nX, 160]], fcs, 'BU');
      W(pg, [[peX, 56], [peX, 160]], fcs, 'GNYE');
      const colour = (tx: number) => (tx === peX ? 'GNYE' : tx === nX ? 'BU' : 'BK');
      if (f.busway) {
        // busbar trunking: connected on the breaker, no terminals, no cable
        for (const tx of [...phases, nX, peX]) W(pg, [[tx, 160], [tx, 200]], fcs, colour(tx));
        S(pg, 'text', x - 4, 208, { props: { text: `⇉ ${$t('Bara kanalı')} ${f.amps} A`, size: 2.3 } });
        S(pg, 'text', x - 4, 213, { props: { text: f.desc || t, size: 2.3 } });
      } else {
        const direct = f.amps > DIRECT_FROM || !termPart(feederSection(f.amps), false);
        for (const tx of [...phases, nX, peX]) {
          if (direct) {
            W(pg, [[tx, 160], [tx, 168]], fcs, colour(tx));
            continue;
          }
          S(pg, 'term', tx, 160, { tag: `X${si + 1}`, part: termPart(feederSection(f.amps), tx === peX), props: { pinNo: termNo(`X${si + 1}`) } });
        }
        for (const tx of [...phases, nX, peX]) W(pg, [[tx, 168], [tx, 200]], fcs, colour(tx));
        // the cable 4 mm beyond its outer conductors: room for the next feeder's cable label
        S(pg, 'cable', x - 4, 184, {
          tag: tag('W'),
          props: { len: peX - x + 8, cableType: 'NYY-J', cores: three ? 'BN,BK,GY,BU,GNYE' : 'BN,BU,GNYE', cs: feederCable(f.amps), ...(f.cableLen ? { length: String(f.cableLen) } : {}) },
        });
        S(pg, 'text', x - 4, 208, { props: { text: `→ ${f.desc || t}`, size: 2.5 } });
      }
      slFeeders.push({ sec: si, t, f, rcd: rcdTag });
      col += w;
    });
    flush();

    /* ---- power factor correction of the section: breaker, contactor, capacitor per step; controller ---- */
    const steps = sec.comp ?? [];
    if (steps.length) {
      const total = steps.reduce((s, k) => s + k, 0);
      const cp = page($t('Kompanzasyon · bölüm {0} · {1} kvar', sec.name!, String(total).replace('.', ',')));
      group.set(cp.id, `fdr${si}`);
      busPages.set(cp.id, sectionAmps(spec, si));
      rows.forEach(([n, y, t, c]) => {
        S(cp, 'brk', 28, y, { rot: 1, props: { name: n, ptype: t } });
        W(cp, [[28, y], [400, y]], '', c);
        S(cp, 'brk', 400, y, { rot: 3, props: { name: n, ptype: t } });
      });
      const km: string[] = [];
      steps.forEach((k, i) => {
        // the step's conductors sized for its breaker (the next standard rating above 1.43 × the capacitor current)
        const x = X(1) + 36 * i, I = capCurrent(k);
        const F = tag('F'), K = tag('K'), C = tag('C');
        const st = I * 1.43 > 63 ? 'mccb3' : 'cb3n';
        const stPart = choose(F, $t('kademe sigortası'), st, { rated: Math.ceil(I * 1.43) });
        const scs = wireCs(ratedOf([...parts, ...(opt.fallback ?? [])].find((q) => q.pn === stPart)?.d ?? '') ?? I * 1.43);
        km.push(K);
        [0, 8, 16].forEach((dx, j) => W(cp, [[x + dx, 24 + 8 * j], [x + dx, 64]], scs, 'BK'));
        // steps above 63 A (from about 30 kvar) on a moulded case breaker: no miniature breaker that big
        S(cp, st, x, 64, { tag: F, desc: $t('Kademe {0}', i + 1), part: stPart });
        [0, 8, 16].forEach((dx) => W(cp, [[x + dx, 88], [x + dx, 100]], scs, 'BK'));
        S(cp, 'k3', x, 100, { tag: K });
        [0, 8, 16].forEach((dx) => W(cp, [[x + dx, 120], [x + dx, 140]], scs, 'BK'));
        S(cp, 'cap3', x, 140, { tag: C, desc: `${String(k).replace('.', ',')} kvar`, part: has(`KND-${k}`) });
      });
      const cc = page($t('Kompanzasyon kumanda · bölüm {0}', sec.name!));
      group.set(cc.id, `fdr${si}`);
      S(cc, 'brk', 32, 24, { rot: 1, props: { name: b.L1, ptype: 'L' } });
      W(cc, [[32, 24], [60, 24], [60, 32]], '1.5', 'BK');
      S(cc, 'cb1', 60, 32, { tag: tag('F'), desc: $t('Kompanzasyon kumanda'), part: has('OS-C6-1P') });
      S(cc, 'brk', 32, 224, { rot: 1, props: { name: b.N, ptype: 'N' } });
      W(cc, [[32, 224], [396, 224]], '1.5', 'BU');
      const xEnd = X(4) + 24 * (steps.length - 1);
      W(cc, [[60, 56], [60, 64], [xEnd, 64]], '1.5', 'RD');
      const KR = tag('K');
      S(cc, 'rpr', 72, 100, { tag: KR, desc: $t('Reaktif güç rölesi'), part: steps.length <= 6 ? has('RGR-6') : has('RGR-12') });
      W(cc, [[72, 64], [72, 100]], '1.5', 'RD');
      W(cc, [[80, 100], [80, 84], [44, 84], [44, 224]], '1.5', 'BU');
      km.forEach((K, i) => {
        const x = X(4) + 24 * i;
        W(cc, [[x, 64], [x, 80]]);
        S(cc, 'no', x, 80, { tag: KR, desc: $t('Kademe {0}', i + 1) });
        W(cc, [[x, 96], [x, 140]]);
        S(cc, 'coil', x, 140, { tag: K, desc: $t('Kademe {0} kontaktörü', i + 1), part: choose(K, $t('kondansatör kontaktörü'), 'coil', { rated: Math.ceil(capCurrent(steps[i]) * 1.43), text: '230 V AC' }) });
        W(cc, [[x, 160], [x, 224]]);
      });
      warnings.push($t('Bölüm {0} kompanzasyonu bir şablondur: reaktif güç rölesinin akım trafosunu bölümün girişine (tüm yükleri gören yere) bağlayın; ön şarj dirençli kondansatör kontaktörü kullanın.', sec.name!));
    }
  });

  /* ---- interlocking of motorised breakers: no two sources in parallel on one bus (unless synchronised) ---- */
  // with controllers the interlocking is theirs (and mechanical); no relay interlock page
  const conflicts = !ctl && !spec.sync && (spec.sections.some((s) => s.sources.length > 1) || (spec.couplers?.length ?? 0) > 0);
  const motorised = brk.filter((x) => x.acb);
  if (conflicts && motorised.length) {
    const c = page($t('Kilitleme kumandası 230 V'));
    // the interlocking relays sit in the coupler compartment, else with the first incomers
    group.set(c.id, spec.couplers?.length ? 'cpl0' : 'src0');
    const b0 = busNames(spec, 0);
    S(c, 'brk', 32, 24, { rot: 1, props: { name: b0.L1, ptype: 'L' } });
    W(c, [[32, 24], [48, 24], [48, 32]], '1.5', 'BK');
    S(c, 'cb1', 48, 32, { tag: tag('F'), desc: $t('Kilitleme kumanda'), part: has('OS-C6-1P') });
    W(c, [[48, 56], [48, 64], [396, 64]], '1.5', 'RD');
    S(c, 'brk', 32, 224, { rot: 1, props: { name: b0.N, ptype: 'N' } });
    W(c, [[32, 224], [396, 224]], '1.5', 'BU');
    const srcOf = (si: number) => brk.filter((x) => x.role === 'src' && x.sec === si).map((x) => x.tag);
    const cplLeft = (si: number) => brk.find((x) => x.role === 'cpl' && x.sec === si - 1)?.tag;
    const cplRight = (si: number) => brk.find((x) => x.role === 'cpl' && x.sec === si)?.tag;
    /** one rung: series NC contacts, then (optionally) parallel branches of NC contacts, then the closing coil */
    let x = X(1) + 8;
    const rung = (q: string, series: string[], branches: string[][]) => {
      let y = 64;
      // each contact: 4 mm of wire, then the contact itself (16 mm), the next one joining at its lower end
      const step = (xx: number, t: string) => {
        W(c, [[xx, y], [xx, y + 4]]);
        S(c, 'nc', xx, y + 4, { tag: t });
        y += 20;
      };
      for (const t of series) step(x, t);
      if (branches.length) {
        const top = y, xs = branches.map((_, i) => x + 24 * i);
        W(c, [[x, top], [xs[xs.length - 1], top]]);
        let bottom = top;
        branches.forEach((br, i) => {
          let yy = top;
          for (const t of br) {
            W(c, [[xs[i], yy], [xs[i], yy + 4]]);
            S(c, 'nc', xs[i], yy + 4, { tag: t });
            yy += 20;
          }
          bottom = Math.max(bottom, yy);
        });
        branches.forEach((br, i) => {
          const yy = top + 20 * br.length;
          if (yy < bottom) W(c, [[xs[i], yy], [xs[i], bottom]]);
        });
        W(c, [[x, bottom], [xs[xs.length - 1], bottom]]);
        y = bottom;
      }
      W(c, [[x, y], [x, 172]]);
      S(c, 'coil_cl', x, 172, { tag: q, desc: $t('{0} kapama izni', q) });
      W(c, [[x, 192], [x, 224]]);
      x += 40 + 24 * Math.max(0, branches.length - 1);
    };
    for (const m of motorised) {
      if (x > 380) {
        warnings.push($t('Kilitleme kumandası bir sayfaya sığmadı; kalan şalterlerin kilitlerini elle ekleyin.'));
        break;
      }
      if (m.role === 'src') {
        // other sources of the same section are open, and the neighbouring sections cannot feed in through a closed coupler
        const series = srcOf(m.sec).filter((t) => t !== m.tag);
        const branches: string[][] = [];
        for (const [cpl, other] of [[cplLeft(m.sec), m.sec - 1], [cplRight(m.sec), m.sec + 1]] as const) {
          if (!cpl) continue;
          const others = srcOf(other);
          if (others.length) branches.push([cpl], others);
        }
        // with couplers on both sides keep it simple: each coupler side as its own pair of branches
        rung(m.tag, series, branches.length > 2 ? branches.slice(0, 2) : branches);
      } else {
        // a coupler closes only when one of the two sections has no source closed
        rung(m.tag, [], [srcOf(m.sec), srcOf(m.sec + 1)].filter((b) => b.length));
      }
    }
    warnings.push($t('Kilitleme kumandası bir şablondur: aynı baraya iki kaynağın paralel bağlanmasını kapama bobinlerinin önündeki açan kontaklarla engeller (kuplajda "üçte iki" mantığı). Mekanik kilidi, açtırma bobinlerini ve beslemenin şebeke kesildiğinde de sürmesini (UPS) kontrol edin.'));
  } else if (conflicts) {
    warnings.push($t('Birden fazla kaynak ya da kuplaj var ama şalterler motorlu değil: kaynakları ve kuplajı mekanik kilit (anahtar kilidi) ile birbirine kilitleyin.'));
  }
  if (spec.sync) warnings.push($t('Kaynaklar paralel (senkron) çalışıyor: senkronizasyon ve yük paylaşımı jeneratör kontrol ünitelerinden yapılır; şalterlerin kapama komutlarını kontrol ünitelerine bağlayın.'));

  /* ---- single-line overview: sections side by side, sources above, feeders below, couplers between ---- */
  /* ---- controllers (synchronising / mains): 24 V DC supply, per source a controller page and a breaker control page ---- */
  if (ctl && units.length) {
    const DCP: [string, PType] = ['+24V', 'DC+'], DCM: [string, PType] = ['0V', 'DC-'];
    const rails = (p: Page, top: number, bottom: number, x1 = 396) => {
      S(p, 'brk', 32, top, { rot: 1, props: { name: DCP[0], ptype: DCP[1] } });
      W(p, [[32, top], [x1, top]], '1', 'DBU');
      S(p, 'brk', 32, bottom, { rot: 1, props: { name: DCM[0], ptype: DCM[1] } });
      W(p, [[32, bottom], [x1, bottom]], '1', 'WH');
    };
    // the supply: from busbar A through a breaker and a power supply; the generators' batteries are not drawn
    const ps = page($t('Kumanda beslemesi 24 V DC'));
    group.set(ps.id, spec.couplers?.length ? 'cpl0' : 'src0');
    const b0 = busNames(spec, 0);
    S(ps, 'brk', 48, 24, { rot: 1, props: { name: b0.L1, ptype: 'L' } });
    S(ps, 'brk', 48, 32, { rot: 1, props: { name: b0.N, ptype: 'N' } });
    W(ps, [[48, 24], [96, 24], [96, 48]], '1.5', 'BK');
    S(ps, 'cb1', 96, 48, { tag: tag('F'), desc: $t('Kumanda beslemesi 24 V DC'), part: has('OS-C6-1P') });
    W(ps, [[96, 72], [96, 96]], '1.5', 'BK');
    W(ps, [[48, 32], [120, 32], [120, 88], [104, 88], [104, 96]], '1.5', 'BU');
    S(ps, 'psu', 96, 96, { tag: tag('G'), desc: $t('Kumanda beslemesi 24 V DC'), part: has('GK-24-5') });
    // backed up by the generator batteries through diodes: the controllers stay supplied when the busbar is dead
    const backup = units.filter((u) => u.kind === 'gen');
    W(ps, [[96, 120], [96, 128]], '1.5', 'DBU');
    S(ps, 'diode', 96, 128, { tag: tag('V'), desc: $t('Ayırma diyodu'), part: has('D-KL-10A') });
    const xs = backup.map((_, k) => 176 + 24 * k);
    W(ps, [[96, 144], [96, 168], ...(xs.length ? ([[xs[xs.length - 1], 168]] as Pt[]) : [])], '1.5', 'DBU');
    W(ps, [[96, 168], [96, 184]], '1.5', 'DBU');
    S(ps, 'brk', 96, 184, { props: { name: DCP[0], ptype: DCP[1] } });
    backup.forEach((u, k) => {
      const x = xs[k];
      S(ps, 'brk', x, 104, { rot: 2, props: { name: `${u.name}.B+`, ptype: 'DC+' } });
      W(ps, [[x, 104], [x, 128]], '1.5', 'RD');
      S(ps, 'diode', x, 128, { tag: tag('V'), desc: `${u.name} ${$t('akü yedeği')}`, part: has('D-KL-10A') });
      W(ps, [[x, 144], [x, 168]], '1.5', 'DBU');
    });
    W(ps, [[104, 120], [104, 132], [136, 132], [136, 144]], '1.5', 'WH');
    S(ps, 'brk', 136, 144, { props: { name: DCM[0], ptype: DCM[1] } });

    for (const u of units) {
      const b = busNames(spec, u.si);
      const A = tag('A');
      /* controller: measuring inputs at the top, digital inputs and outputs on the 24 V rails below */
      const cp = page($t('{0} kontrolörü · {1}', u.name, ctl.model));
      group.set(cp.id, group.get(u.page)!);
      // a coupler's controller (bus tie) is numbered generically: its breaker terminals depend on the application
      const known = CTL_TERMINALS.find((c) => c.match.test(ctl.model)), io = u.kind === 'cpl' ? undefined : known?.[u.kind === 'gen' ? 'gen' : 'mains'];
      S(cp, 'gctl', 64, 72, { tag: A, desc: `${u.name} · ${ctl.model}`, ...(known ? { props: { pins: known.ac } } : {}) });
      // load sharing between the controllers (CAN) and the operator panel (RS485): lines through all controller pages
      (
        [
          [224, 'CAN-H'],
          [240, 'CAN-L'],
          [264, 'RS485-A'],
          [280, 'RS485-B'],
        ] as [number, string][]
      ).forEach(([x, name]) => {
        W(cp, [[x, 108], [x, 114]], '0.5', 'VT');
        S(cp, 'brk', x, 114, { props: { name, ptype: '' } });
      });
      const into = (x: number, name: string, pt: PType, fused: boolean, col: string) => {
        S(cp, 'brk', x, 24, { rot: 2, props: { name, ptype: pt } });
        if (fused) {
          W(cp, [[x, 24], [x, 36]], '1', col);
          S(cp, 'fuse', x, 36, { tag: tag('F'), part: has('SG-2A-5x20') });
          W(cp, [[x, 52], [x, 72]], '1', col);
        } else W(cp, [[x, 24], [x, 72]], pt === '' ? '2.5' : '1', col);
      };
      ['I1', 'I2', 'I3', 'IC'].forEach((s, i) => into(64 + 16 * i, `${u.name}.${s}`, '', false, 'BK'));
      ['UG1', 'UG2', 'UG3', 'UGN'].forEach((s, i) => into(136 + 16 * i, `${u.name}.${s}`, i < 3 ? 'L' : 'N', false, i < 3 ? 'BK' : 'BU'));
      [b.L1, b.L2, b.L3, b.N].forEach((s, i) => into(208 + 16 * i, s, i < 3 ? 'L' : 'N', i < 3, i < 3 ? 'BK' : 'BU'));
      // the controller's own supply from the 24 V rails
      S(cp, 'brk', 280, 24, { rot: 2, props: { name: DCP[0], ptype: DCP[1] } });
      W(cp, [[280, 24], [280, 72]], '1', 'DBU');
      S(cp, 'brk', 296, 24, { rot: 2, props: { name: DCM[0], ptype: DCM[1] } });
      W(cp, [[296, 24], [296, 72]], '1', 'WH');
      rails(cp, 128, 224);
      // digital inputs: breaker position from its auxiliary contacts, emergency stop and reset on the door, and for a
      // generator the engine fault and its protection breaker through relays from the field (its own battery)
      const gen = u.kind === 'gen';
      const kFault = gen ? tag('K') : '', kProt = gen ? tag('K') : '';
      const ins: [string, string, string, string][] = [
        ['no', u.Q, $t('Şalter kapalı'), ''],
        ['nc', u.Q, $t('Şalter açık'), ''],
        ['estop', tag('S'), $t('Acil stop'), `${u.name} ${$t('acil stop')}`],
        ['pb_no', tag('S'), $t('Arıza reset'), `${u.name} ${$t('arıza reset')}`],
        ...(gen
          ? ([
              ['no', kFault, $t('Motor arıza'), ''],
              ['no', kProt, $t('Koruma şalteri'), ''],
            ] as [string, string, string, string][])
          : []),
      ];
      let x = 48, di = 0, dq = 0;
      for (const [type, t, d, sd] of ins) {
        W(cp, [[x, 128], [x, 140]], '1', 'DBU');
        S(cp, type, x, 140, { tag: t, desc: sd });
        W(cp, [[x, 156], [x, 168]], '1', 'DBU');
        S(cp, 'plcin', x, 168, { tag: A, desc: d, props: { addr: io?.di[di] || `DI${di + 1}` } });
        di++;
        x += 24;
      }
      // digital outputs: start, close and open the breaker through relays, running and alarm lamps on the door
      const kStart = gen ? tag('K') : '', kClose = tag('K'), kOpen = tag('K');
      const outs: [string, string, string, string, string][] = [
        ...(gen ? [['coil', kStart, $t('Motor start'), `${u.name} ${$t('start rölesi')}`, ''] as [string, string, string, string, string]] : []),
        ['coil', kClose, $t('Şalter kapat'), `${u.Q} ${$t('kapatma rölesi')}`, ''],
        ['coil', kOpen, $t('Şalter aç'), `${u.Q} ${$t('açma rölesi')}`, ''],
        ['lamp_led', tag('H'), $t('Çalışıyor'), `${u.name} ${$t('çalışıyor')}`, $t('yeşil')],
        ['lamp_led', tag('H'), $t('Arıza'), `${u.name} ${$t('arıza')}`, $t('kırmızı')],
      ];
      x += 16;
      for (const [type, t, d, ld, colour] of outs) {
        S(cp, 'plcout', x, 140, { tag: A, desc: d, props: { addr: io?.dq[dq] || `DO${dq + 1}` } });
        dq++;
        W(cp, [[x, 152], [x, 164]], '1', 'DBU');
        S(cp, type, x, 164, { tag: t, desc: colour ? `${ld} · ${colour}` : ld, ...(type === 'coil' ? { part: has('RL-2CO-24DC') } : {}) });
        W(cp, [[x, type === 'coil' ? 184 : 180], [x, 224]], '1', 'WH');
        x += 24;
      }
      // the start command to the engine panel: a free contact of the start relay on terminals, the field side to the cable
      if (gen) {
        const xs = x + 8, X = `X${spec.sections.length + 1}`;
        W(cp, [[xs, 136], [xs + 16, 136]], '1', 'OG');
        S(cp, 'term', xs, 136, { tag: X, part: has('KL-2.5'), props: { pinNo: termNo(X) } });
        W(cp, [[xs, 144], [xs, 160]], '1', 'OG');
        S(cp, 'no', xs, 160, { tag: kStart });
        W(cp, [[xs, 176], [xs, 188]], '1', 'OG');
        S(cp, 'term', xs, 188, { tag: X, part: has('KL-2.5'), props: { pinNo: termNo(X) } });
        W(cp, [[xs, 196], [xs + 16, 196]], '1', 'OG');
        S(cp, 'text', xs + 6, 168, { props: { text: $t('→ motor kontrol: start'), size: 2.5 } });
      }

      /* field signals of a generator: battery charger, engine fault and protection breaker through relays, speed and voltage bias */
      if (gen) {
        const fp = page($t('{0} saha sinyalleri', u.name));
        group.set(fp.id, group.get(u.page)!);
        const X = `X${spec.sections.length + 1}`;
        const field = (x: number, y: number, text?: string) => {
          S(fp, 'term', x, y, { tag: X, part: has('KL-2.5'), props: { pinNo: termNo(X) } });
          if (text) S(fp, 'text', x - 4, y + 28, { props: { text, size: 2.5 } });
        };
        // the engine battery charger from the busbar: breaker, charger, breaker, to the battery on the engine
        S(fp, 'brk', 40, 24, { rot: 1, props: { name: b.L1, ptype: 'L' } });
        S(fp, 'brk', 40, 32, { rot: 1, props: { name: b.N, ptype: 'N' } });
        W(fp, [[40, 24], [64, 24], [64, 40]], '1.5', 'BK');
        S(fp, 'cb1', 64, 40, { tag: tag('F'), desc: $t('Akü şarj'), part: has('OS-C6-1P') });
        W(fp, [[64, 64], [64, 80]], '1.5', 'BK');
        W(fp, [[40, 32], [88, 32], [88, 72], [72, 72], [72, 80]], '1.5', 'BU');
        S(fp, 'rect', 64, 80, { tag: tag('G'), desc: `${u.name} ${$t('akü şarj redresörü')}`, part: has('AKS-24-10') });
        W(fp, [[64, 104], [64, 112]], '2.5', 'RD');
        S(fp, 'cb1', 64, 112, { tag: tag('F'), desc: $t('Akü şarj çıkışı'), part: has('OS-C10-1P') });
        W(fp, [[64, 136], [64, 152]], '2.5', 'RD');
        W(fp, [[72, 104], [72, 140], [80, 140], [80, 152]], '2.5', 'BK');
        field(64, 152);
        field(80, 152, `→ ${u.name} ${$t('akü')} + / −`);
        // the battery + backs up the 24 V supply, its − is the common 0 V
        W(fp, [[64, 144], [48, 144]], '1.5', 'RD');
        S(fp, 'brk', 48, 144, { rot: 1, props: { name: `${u.name}.B+`, ptype: 'DC+' } });
        W(fp, [[80, 146], [96, 146]], '1.5', 'WH');
        S(fp, 'brk', 96, 146, { rot: 3, props: { name: DCM[0], ptype: DCM[1] } });
        W(fp, [[64, 160], [64, 172]], '2.5', 'RD');
        W(fp, [[80, 160], [80, 172]], '2.5', 'BK');
        // engine fault and protection breaker: a free contact on the engine closes a relay of this panel
        S(fp, 'brk', 136, 40, { rot: 1, props: { name: DCP[0], ptype: DCP[1] } });
        W(fp, [[136, 40], [264, 40]], '1', 'DBU');
        S(fp, 'brk', 136, 224, { rot: 1, props: { name: DCM[0], ptype: DCM[1] } });
        W(fp, [[136, 224], [264, 224]], '1', 'WH');
        (
          [
            [168, kFault, $t('motor arıza')],
            [232, kProt, $t('koruma şalteri')],
          ] as [number, string, string][]
        ).forEach(([x, k, what]) => {
          W(fp, [[x, 40], [x, 56]], '1', 'DBU');
          field(x, 56);
          W(fp, [[x, 64], [x + 12, 64]], '1', 'OG');
          S(fp, 'text', x + 14, 82, { props: { text: `↔ ${u.name} ${what}`, size: 2.5 } });
          W(fp, [[x + 12, 96], [x, 96]], '1', 'OG');
          field(x, 96);
          W(fp, [[x, 104], [x, 120]], '1', 'OG');
          S(fp, 'coil', x, 120, { tag: k, desc: `${u.name} ${what}`, part: has('RL-2CO-24DC') });
          W(fp, [[x, 140], [x, 224]], '1', 'WH');
        });
        // speed (governor) and voltage (AVR) bias from the controller's analogue outputs
        const ao = io && 'ao' in io ? (io.ao as string[]) : [];
        (
          [
            [296, $t('Hız ayarı +')],
            [320, $t('Hız ayarı −')],
            [352, $t('Gerilim ayarı +')],
            [376, $t('Gerilim ayarı −')],
          ] as [number, string][]
        ).forEach(([x, d], i) => {
          S(fp, 'plcao', x, 40, { tag: A, desc: d, props: { addr: ao[i] || `AO${i + 1}` } });
          W(fp, [[x, 52], [x, 72]], '1', 'VT');
          field(x, 72);
          W(fp, [[x, 80], [x, 92]], '1', 'VT');
        });
        S(fp, 'text', 292, 108, { props: { text: `→ ${u.name} ${$t('hız regülatörü / AVR')}`, size: 2.5 } });
      }

      /* breaker control: closing and shunt trip coils through the controller's relays, spring motor, position lamps */
      const bp = page($t('{0} şalter kumandası {1} · 24 V DC', u.Q, u.name));
      group.set(bp.id, group.get(u.page)!);
      rails(bp, 24, 224, 300);
      W(bp, [[48, 24], [48, 32]], '1', 'DBU');
      S(bp, 'cb1', 48, 32, { tag: tag('F'), desc: `${u.Q} ${$t('kumanda')}`, part: has('OS-C6-1P') });
      W(bp, [[48, 56], [48, 64], [240, 64]], '1', 'DBU');
      const col = (cx: number, top: [string, string, string] | null, load: [string, string, string, string?]) => {
        if (top) {
          W(bp, [[cx, 64], [cx, 80]], '1', 'DBU');
          S(bp, top[0], cx, 80, { tag: top[1], desc: top[2] });
          W(bp, [[cx, 96], [cx, 120]], '1', 'DBU');
        } else W(bp, [[cx, 64], [cx, 120]], '1', 'DBU');
        S(bp, load[0], cx, 120, { tag: load[1], desc: load[3] ? `${load[2]} · ${load[3]}` : load[2] });
        W(bp, [[cx, load[0] === 'lamp_led' ? 136 : 140], [cx, 224]], '1', 'WH');
      };
      col(80, ['no', kClose, ''], ['coil_cl', u.Q, $t('Kapama bobini')]);
      col(112, ['no', kOpen, ''], ['coil_st', u.Q, $t('Açtırma bobini')]);
      col(144, null, ['mot_ch', u.Q, $t('Kurma motoru')]);
      col(176, ['no', u.Q, ''], ['lamp_led', tag('H'), `${u.Q} ${$t('kapalı')}`, $t('yeşil')]);
      col(208, ['nc', u.Q, ''], ['lamp_led', tag('H'), `${u.Q} ${$t('açık')}`, $t('kırmızı')]);
    }
    const common = spec.couplers?.length ? 'cpl0' : 'src0';

    /* operator panel on RS485, CAN load sharing line with its 120 Ω terminations */
    const hp = page($t('HMI, haberleşme ve alarm'));
    group.set(hp.id, common);
    (
      [
        [64, DCP[0], DCP[1], true],
        [72, DCM[0], DCM[1], false],
        [80, b0.PE, 'PE', false],
        [96, 'RS485-A', '', false],
        [104, 'RS485-B', '', false],
      ] as [number, string, PType, boolean][]
    ).forEach(([x, name, pt, fused]) => {
      S(hp, 'brk', x, 24, { rot: 2, props: { name, ptype: pt } });
      if (fused) {
        W(hp, [[x, 24], [x, 48]], '1', 'DBU');
        S(hp, 'fuse', x, 48, { tag: tag('F'), part: has('SG-2A-5x20') });
        W(hp, [[x, 64], [x, 120]], '1', 'DBU');
      } else W(hp, [[x, 24], [x, 120]], name === b0.PE ? '1.5' : '0.5', name === b0.PE ? 'GNYE' : pt === 'DC-' ? 'WH' : 'VT');
    });
    S(hp, 'hmi', 64, 120, { tag: tag('A'), desc: $t('Operatör paneli') });
    [200, 240].forEach((x) => {
      S(hp, 'brk', x, 40, { rot: 2, props: { name: 'CAN-H', ptype: '' } });
      W(hp, [[x, 40], [x, 64]], '0.5', 'VT');
      S(hp, 'res', x, 64, { tag: tag('R'), desc: '120 Ω', part: has('R-120-KL') });
      W(hp, [[x, 80], [x, 104]], '0.5', 'VT');
      S(hp, 'brk', x, 104, { props: { name: 'CAN-L', ptype: '' } });
    });
    S(hp, 'text', 188, 124, { props: { text: $t('CAN yük paylaşımı: kontrolörler arasında, iki uçta 120 Ω'), size: 2.5 } });
    // the auxiliary supply watched by a voltage relay: on loss its closed contact lights a lamp and sounds the
    // siren from the battery backed 24 V
    const K = tag('K');
    [b0.L1, b0.L2, b0.L3, b0.N].forEach((name, i) => {
      const x = 300 + 8 * i;
      S(hp, 'brk', x, 24, { rot: 2, props: { name, ptype: i < 3 ? 'L' : 'N' } });
      W(hp, [[x, 24], [x, 32]], '1', i < 3 ? 'BK' : 'BU');
      S(hp, 'fuse', x, 32, { tag: tag('F'), part: has('SG-2A-5x20') });
      W(hp, [[x, 48], [x, 56]], '1', i < 3 ? 'BK' : 'BU');
    });
    S(hp, 'vmr', 300, 56, { tag: K, desc: $t('İç ihtiyaç gerilim izleme'), part: has('GIR-3F') });
    S(hp, 'brk', 300, 120, { rot: 2, props: { name: DCP[0], ptype: DCP[1] } });
    W(hp, [[300, 120], [300, 136]], '1', 'DBU');
    S(hp, 'nc', 300, 136, { tag: K });
    W(hp, [[300, 152], [300, 176]], '1', 'DBU');
    W(hp, [[300, 168], [332, 168], [332, 176]], '1', 'DBU');
    S(hp, 'lamp_led', 300, 176, { tag: tag('H'), desc: `${$t('İç ihtiyaç kesik')} · ${$t('kırmızı')}` });
    S(hp, 'horn', 332, 176, { tag: tag('H'), desc: $t('Alarm sireni') });
    W(hp, [[300, 192], [300, 208], [332, 208], [332, 192]], '1', 'WH');
    W(hp, [[316, 208], [316, 216]], '1', 'WH');
    S(hp, 'brk', 316, 216, { props: { name: DCM[0], ptype: DCM[1] } });

    /* auxiliary supply of the panel: compartment lights on door switches, heaters on thermostats, a socket */
    const ap = page($t('İç ihtiyaç · aydınlatma, ısıtıcı, priz'));
    group.set(ap.id, common);
    // from the busbar through its own breaker onto the rail (2.5 mm²: the socket's 16 A breaker hangs on it too)
    S(ap, 'brk', 32, 24, { rot: 1, props: { name: b0.L1, ptype: 'L' } });
    W(ap, [[32, 24], [44, 24]], '2.5', 'BK');
    S(ap, 'cb1', 44, 24, { tag: tag('F'), desc: $t('İç ihtiyaç ana sigortası'), part: choose('F', $t('iç ihtiyaç ana sigortası'), 'cb1', { rated: 20 }) });
    W(ap, [[44, 48], [392, 48]], '2.5', 'BK');
    S(ap, 'brk', 32, 216, { rot: 1, props: { name: b0.N, ptype: 'N' } });
    W(ap, [[32, 216], [392, 216]], '2.5', 'BU');
    const n = Math.min(4, Math.max(2, units.length));
    let ax = 64;
    const branch = (dev: [string, string, string], load: [string, string, number, string]) => {
      W(ap, [[ax, 48], [ax, 64]], '1.5', 'BK');
      S(ap, 'cb1', ax, 64, { tag: tag('F'), desc: load[1], part: choose('F', $t('iç ihtiyaç sigortası'), 'cb1', { rated: 6 }) });
      W(ap, [[ax, 88], [ax, 104]], '1.5', 'BK');
      S(ap, dev[0], ax, 104, { tag: tag(dev[1]), part: has(dev[2]) });
      W(ap, [[ax, 120], [ax, 144]], '1.5', 'BK');
      S(ap, load[0], ax, 144, { tag: tag('E'), desc: load[1], part: has(load[3]) });
      W(ap, [[ax, 144 + load[2]], [ax, 216]], '1.5', 'BU');
      ax += 32;
    };
    for (let i = 0; i < n; i++) branch(['ls_no', 'S', 'KAP-SW-1NO'], ['lamp_tube', $t('Pano aydınlatma'), 20, 'LED-PA-230']);
    // the heater's thermostat opens when the compartment is warm enough (break contact, IEC 60617 S00264)
    for (let i = 0; i < n; i++) branch(['tstat_nc', 'B', 'TS-NC-0-60'], ['heat', $t('Pano ısıtıcı'), 16, 'ISI-50']);
    // the socket: L from its breaker, N to the rail, PE from the earth bar
    W(ap, [[ax, 48], [ax, 64]], '2.5', 'BK');
    S(ap, 'cb1', ax, 64, { tag: tag('F'), desc: $t('Pano içi priz'), part: choose('F', $t('priz sigortası'), 'cb1', { rated: 16 }) });
    W(ap, [[ax, 88], [ax, 160]], '2.5', 'BK');
    S(ap, 'socket', ax, 160, { tag: tag('X'), desc: $t('Pano içi priz'), part: has('PRZ-RAY-16') });
    S(ap, 'brk', ax + 8, 148, { rot: 2, props: { name: b0.PE, ptype: 'PE' } });
    W(ap, [[ax + 8, 148], [ax + 8, 160]], '2.5', 'GNYE');
    W(ap, [[ax + 16, 160], [ax + 16, 216]], '2.5', 'BU');

    /* engine heaters of the generators: a breaker each, to the engine on terminals */
    const gens = units.filter((u) => u.kind === 'gen');
    if (gens.length) {
      const gp = page($t('Jeneratör ısıtıcı beslemeleri'));
      group.set(gp.id, common);
      const X = `X${spec.sections.length + 1}`;
      (
        [
          [24, b0.L1, 'L', 'BK'],
          [32, b0.N, 'N', 'BU'],
          [40, b0.PE, 'PE', 'GNYE'],
        ] as [number, string, PType, string][]
      ).forEach(([y, name, pt, col]) => {
        S(gp, 'brk', 32, y, { rot: 1, props: { name, ptype: pt } });
        W(gp, [[32, y], [56 + 48 * (gens.length - 1) + 16, y]], '2.5', col);
      });
      gens.forEach((u, i) => {
        const x = 56 + 48 * i;
        W(gp, [[x, 24], [x, 56]], '4', 'BK');
        S(gp, 'cb1', x, 56, { tag: tag('F'), desc: `${u.name} ${$t('ısıtıcı')}`, part: choose('F', $t('ısıtıcı sigortası'), 'cb1', { rated: 25 }) });
        W(gp, [[x, 80], [x, 120]], '4', 'BK');
        W(gp, [[x + 8, 32], [x + 8, 120]], '4', 'BU');
        W(gp, [[x + 16, 40], [x + 16, 120]], '4', 'GNYE');
        for (const tx of [x, x + 8, x + 16]) {
          S(gp, 'term', tx, 120, { tag: X, part: has('KL-2.5'), props: { pinNo: termNo(X) } });
          W(gp, [[tx, 128], [tx, 140]], '4', tx === x ? 'BK' : tx === x + 8 ? 'BU' : 'GNYE');
        }
        S(gp, 'text', x - 4, 150, { props: { text: `→ ${u.name} ${$t('motor ısıtıcısı')}`, size: 2.5 } });
      });
    }

    warnings.push(
      CTL_TERMINALS.some((c) => c.match.test(ctl.model))
        ? $t('{0} kontrol sayfaları uç numaralarıyla çizildi (yapılandırılabilir giriş ve çıkışları kontrolör ayarlarıyla eşleştirin); şalterin bobin gerilimini kontrol edin.', ctl.model)
        : $t('{0} kontrol sayfaları genel uç numaralarıyla (DI, DO, AO) çizildi: seçtiğiniz kontrolörün uç numaralarını ve şalterin bobin gerilimini girin.', ctl.model),
    );
    if (units.some((u) => u.kind === 'cpl')) warnings.push($t('Kuplaj kontrolörünün giriş ve çıkışları genel numaralı: kuplaj (bus tie) uygulamasının uçlarını kontrolörün belgesinden girin.'));
  }

  if (opt.singleLine !== false) {
    const BUS_Y = 72;
    // sections side by side; a section that would run past the right edge starts a new page, its coupler going over
    // through a pair of interruption points
    const pageOf: number[] = [];
    {
      let px = 36, pg = 0;
      spec.sections.forEach((sec, si) => {
        const span = 24 * Math.max(sec.sources.length, slFeeders.filter((f) => f.sec === si).length, 1) - 16;
        if (si && px + span > 404) {
          pg++;
          px = 36;
        }
        pageOf.push(pg);
        px += span + (spec.couplers?.[si] && si < spec.sections.length - 1 ? 64 : 16);
      });
    }
    const nPages = pageOf[pageOf.length - 1] + 1;
    let sp!: Page;
    const SL = (type: string, x: number, y: number, t = '', props: SymProps = {}, desc = '', rot = 0) => sp.syms.push({ id: newId(), type, x, y, rot, tag: t, desc, part: '', maker: '', props });
    const WL = (pts: Pt[]) => sp.wires.push({ id: newId(), pts, cs: '', col: '' });
    const newPage = (k: number) => {
      sp = { id: newId(), desc: $t('Tek hat şeması · {0}', spec.title || $t('pano')) + (nPages > 1 ? ` (${k + 1}/${nPages})` : ''), kind: 'single', syms: [], wires: [] };
      pages.push(sp);
      SL('text', 20, 18, '', { text: '3/N/PE ~ 400/230 V 50 Hz', size: 2.5 });
    };
    newPage(0);
    let x = 36, busFrom: number | undefined, carry = '';
    let wide = false;
    const srcTags = brk.filter((b) => b.role === 'src');
    spec.sections.forEach((sec, si) => {
      if (si && pageOf[si] !== pageOf[si - 1]) {
        newPage(pageOf[si]);
        x = 36;
        busFrom = undefined;
        if (carry) {
          // the coupler from the previous page comes down onto this section's busbar, left of its first source
          SL('brk', 24, BUS_Y - 32, '', { name: carry }, '', 2);
          WL([[24, BUS_Y - 32], [24, BUS_Y]]);
          busFrom = 24;
          carry = '';
        }
      }
      const x0 = x;
      sec.sources.forEach((src, i) => {
        const q = srcTags.filter((b) => b.sec === si)[i]?.tag ?? '';
        SL('text', x - 4, 22, '', { text: src.name, size: 2.5 });
        WL([[x, 26], [x, 34]]);
        SL('sl_3p', x, 30);
        SL('sl_cb', x, 34, q, { amps: String(src.amps), swkind: src.sw, poles: src.poles4 ? '4' : '3', source: src.kind }, src.name);
        if (src.meter || opt.control) {
          // metered incomer: three current transformers under the breaker
          WL([[x, 50], [x, 52]]);
          SL('sl_ct', x, 52);
          SL('text', x - 7.5, 61, '', { text: '3×', size: 2 });
          WL([[x, 68], [x, BUS_Y]]);
        } else WL([[x, 50], [x, BUS_Y]]);
        x += 24;
      });
      const fs = slFeeders.filter((f) => f.sec === si);
      const fx0 = Math.max(x0, x - 24 * sec.sources.length);
      fs.forEach(({ t, f, rcd }, i) => {
        const fx = fx0 + 24 * i;
        let y = BUS_Y;
        WL([[fx, y], [fx, y + 12]]);
        if (f.poles !== 1) SL('sl_3p', fx, y + 6);
        SL('sl_cb', fx, y + 12, t, { amps: String(f.amps), poles: f.poles === 1 ? '1' : f.poles === 4 ? '4' : '3', ...(f.busway ? { busway: '1' } : {}), ...(f.cableLen ? { length: String(f.cableLen) } : {}) }, f.desc);
        y += 28;
        if (rcd) {
          WL([[fx, y], [fx, y + 4]]);
          SL('sl_rcd', fx, y + 4, rcd, { amps: String(f.amps) });
          y += 20;
        }
        WL([[fx, y], [fx, y + 24]]);
        [`${f.amps} A${f.poles === 4 ? ' 4P' : f.poles === 1 ? ' 1P' : ''}`, f.busway ? $t('bara kanalı') : `${feederCable(f.amps)} mm²`]
          .filter(Boolean)
          .forEach((tx, j) => SL('text', fx + 2, y + 6 + 4.5 * j, '', { text: tx, size: 1.8 }));
      });
      const xEnd = Math.max(x - 24, fx0 + 24 * Math.max(fs.length - 1, 0)) + 8;
      WL([[Math.min(x0 - 8, busFrom ?? Infinity), BUS_Y], [xEnd, BUS_Y]]);
      busFrom = undefined;
      SL('text', x0 - 8, BUS_Y - 3, '', { text: $t('Bölüm {0} · {1} A', sec.name!, sectionAmps(spec, si)), size: 2 });
      x = xEnd + 16;
      const c = spec.couplers?.[si];
      if (c && si < spec.sections.length - 1) {
        // coupler: a loop over the gap between the two busbars (feeders hang below), the breaker in its rising leg
        const q = brk.find((b) => b.role === 'cpl' && b.sec === si)?.tag ?? '';
        const cx = xEnd + 16, xB = cx + 16;
        WL([[xEnd, BUS_Y], [xEnd, BUS_Y - 44], [cx, BUS_Y - 44], [cx, BUS_Y - 40]]);
        SL('sl_cb', cx, BUS_Y - 40, q, { amps: String(c.amps), swkind: c.sw, coupler: '1', poles: c.poles4 ? '4' : '3' }, $t('Kuplaj'));
        if (pageOf[si + 1] !== pageOf[si]) {
          // the next section is on the next page: the coupler's leg ends in an interruption point
          carry = `${q || $t('Kuplaj')} → ${spec.sections[si + 1].name}`;
          WL([[cx, BUS_Y - 24], [cx, BUS_Y - 12], [cx + 12, BUS_Y - 12]]);
          SL('brk', cx + 12, BUS_Y - 12, '', { name: carry }, '', 3);
        } else {
          WL([[cx, BUS_Y - 24], [cx, BUS_Y - 12], [xB, BUS_Y - 12], [xB, BUS_Y]]);
          busFrom = xB;
          x = xB + 32;
        }
      }
      if (xEnd > 410) wide = true;
    });
    if (wide) warnings.push($t('Tek hat özeti sayfa genişliğini aşıyor; sayfayı A3 yatay basın ya da bölümleri ayrı sayfalara bölün.'));
  }

  /* ---- copper busbar marking: incoming circuits and busbars of sections from 400 A ---- */
  for (const pg of pages) {
    const amps = busPages.get(pg.id);
    if (!amps || amps < BUSBAR_FROM) continue;
    const ph = busbarSize(amps), pe = busbarSize(amps / 2), nb = spec.nHalf ? busbarSize(amps / 2) : ph;
    const isSourceOrCoupler = pg.desc.includes($t('girişi')) || pg.desc.startsWith($t('Kuplaj'));
    for (const w of pg.wires) {
      const isMain = isSourceOrCoupler && w.cs === '' && ['BK', 'BU', 'GNYE'].includes(w.col);
      const isBus = w.pts.length === 2 && w.pts[0][0] === 28 && w.pts[1][0] === 400 && w.pts[0][1] === w.pts[1][1] && w.pts[0][1] <= 56;
      if (isMain || isBus) w.bus = w.col === 'GNYE' ? pe : w.col === 'BU' ? nb : ph;
    }
  }
  const maxA = Math.max(...spec.sections.map((_, i) => sectionAmps(spec, i)));
  if (maxA >= BUSBAR_FROM)
    warnings.push(
      $t('Ana devre bakır bara ile çizildi: faz {0}, nötr {1}, PE {2} mm Cu (en büyük bölüm). Bara mesnetlerini ve kısa devre dayanımını üreticinin sistemine göre kontrol edin.', busbarSize(maxA), spec.nHalf ? busbarSize(maxA / 2) : busbarSize(maxA), busbarSize(maxA / 2)),
    );
  // compartments as in the front view: incomers at both ends, feeders towards the middle, each coupler between
  // its two sections (A incomers, A feeders, coupler, B feeders, B incomers)
  const n = spec.sections.length, order: string[] = [];
  spec.sections.forEach((_, si) => {
    order.push(...(n > 1 && si === n - 1 ? [`fdr${si}`, `src${si}`] : [`src${si}`, `fdr${si}`]));
    if (si < n - 1) order.push(`cpl${si}`);
  });
  const nameOf = (k: string) => {
    const i = +k.slice(3), s = spec.sections[i]?.name ?? '';
    if (k.startsWith('cpl')) return $t('Kuplaj {0}–{1}', s, spec.sections[i + 1]?.name ?? '');
    const what = k.startsWith('src') ? $t('Girişler') : $t('Çıkışlar');
    return n > 1 ? `${s} · ${what}` : what;
  };
  // two incomers to a compartment, one under and one over the busbars (as in the Lujo and Lord Palace boards)
  const groups = order
    .map((k) => ({ name: nameOf(k), bus: k.startsWith('cpl') ? -1 : +k.slice(3), pages: pages.filter((p) => group.get(p.id) === k).map((p) => p.id) }))
    .filter((g) => g.pages.length);
  return { pages, warnings, groups };
}

/* ---------------- board description as text ---------------- */

const fold = (s: string) => s.toLocaleLowerCase('tr').replace(/ı/g, 'i').replace(/ş/g, 's').replace(/ğ/g, 'g').replace(/ü/g, 'u').replace(/ö/g, 'o').replace(/ç/g, 'c');
const swOf = (t: string): IncomerKind | null => {
  const f = fold(t);
  return /^(acb|acik tip|hava)/.test(f) ? 'acb' : /^(mccb|kompakt)/.test(f) ? 'mccb' : /^(lbs|yuk ayirici|yuk kesici|ayirici)/.test(f) ? 'lbs' : null;
};
const ampsOf = (t: string) => {
  const m = /^(\d{1,4}(?:[.,]\d+)?)\s*a$/i.exec(t.trim()) ?? /^(\d{1,4})$/.exec(t.trim());
  return m ? Number(m[1].replace(',', '.')) : null;
};
const cells = (line: string) => line.split(line.includes('\t') ? '\t' : ';').map((c) => c.trim());

/**
 * Reads a board described line by line: "Bölüm A" starts a section, "GİRİŞ …" is a source (name; trafo /
 * jeneratör / hat; current; ACB / MCCB / yük ayırıcı; 4P; ölçüm; note), "KUPLAJ …" couples the section to the
 * next one, "KOMP 5, 10" adds capacitor steps, "PARAFUDR" surge protection, "Senkron" makes the sources run in
 * parallel, every other line is a feeder (tag; description; current; 1P / 3P / 4P; kaçak akım; length m;
 * bara kanalı). Separators: tab or ";".
 */
export function parseBoardText(text: string): { spec: BoardSpec; errors: string[] } {
  const errors: string[] = [];
  const sections: SectionSpec[] = [];
  const couplers: NonNullable<BoardSpec['couplers']> = [];
  let sync = false, nHalf = false, title = '';
  const cur = () => {
    if (!sections.length) sections.push({ sources: [], feeders: [] });
    return sections[sections.length - 1];
  };
  text.split(/\r?\n/).forEach((raw, i) => {
    const line = raw.trim();
    if (!line || line.startsWith('//')) return;
    const f = fold(line);
    const sec = /^(#|bolum|section)\s*:?\s*(.*)$/.exec(f);
    if (sec) {
      const name = line.slice(line.length - sec[2].length).trim();
      sections.push({ ...(name ? { name: name.slice(0, 12) } : {}), sources: [], feeders: [] });
      return;
    }
    if (/^(pano|board|baslik)\s*:/.test(f)) {
      title = line.replace(/^[^:]*:\s*/, '').slice(0, 80);
      return;
    }
    if (/^(senkron|sync|paralel)\b/.test(f) && !line.includes(';') && !line.includes('\t')) {
      sync = true;
      return;
    }
    // "Nötr: yarım kesit" / "Nötr 1:1"
    if (/^(notr|neutral)\b/.test(f) && !line.includes(';') && !line.includes('\t')) {
      nHalf = /(yari|1\/2|half|%\s*50|50\s*%)/.test(f);
      return;
    }
    if (/^(parafudr|spd|asiri gerilim)/.test(f)) {
      cur().spd = true;
      return;
    }
    const comp = /^(kompanzasyon|komp|pfc)\s*:?\s*(.*)$/.exec(f);
    if (comp && !line.includes(';') && !line.includes('\t')) {
      const r = parseSteps(line.slice(line.length - comp[2].length));
      if (r.error) errors.push($t('Satır {0}: {1}', i + 1, r.error));
      else cur().comp = r.steps;
      return;
    }
    const kw = /^(giris|kaynak|source|incomer|kuplaj|coupler)\s*:?\s*/.exec(f);
    const body = kw ? line.slice(kw[0].length) : line;
    const cs = cells(body).filter(Boolean);
    if (kw && /^(kuplaj|coupler)/.test(kw[1])) {
      let amps = 0, sw: IncomerKind | null = null, poles4 = false;
      for (const c of cs) {
        amps = ampsOf(c) ?? amps;
        sw = swOf(c) ?? sw;
        if (/^4\s*p$/i.test(c)) poles4 = true;
      }
      if (!amps) errors.push($t('Satır {0}: kuplaj akımı yok', i + 1));
      cur();
      couplers[sections.length - 1] = { amps: amps || 630, sw: sw ?? (amps > 630 ? 'acb' : 'mccb'), ...(poles4 ? { poles4 } : {}) };
      return;
    }
    if (kw) {
      let name = '', kind: SourceKind | null = null, amps = 0, sw: IncomerKind | null = null, poles4 = false, meter = false;
      const rest: string[] = [];
      cs.forEach((c, j) => {
        const fc = fold(c), a = ampsOf(c);
        if (a != null) amps = a;
        else if (swOf(c)) sw = swOf(c);
        else if (/^4\s*p$/i.test(c)) poles4 = true;
        else if (/^3\s*p$/i.test(c)) poles4 = false;
        else if (/^(olcum|olcu|meter|analizor|sayac)/.test(fc)) meter = true;
        else if (/^(trafo|transformator|tr)$/.test(fc)) kind = 'tr';
        else if (/^(jenerator|jen|gen|generator)$/.test(fc)) kind = 'gen';
        else if (/^(hat|line|besleme|senkron hatti)$/.test(fc)) kind = 'line';
        else if (j === 0) name = c.slice(0, 20);
        else rest.push(c);
      });
      if (!amps) errors.push($t('Satır {0}: giriş akımı yok', i + 1));
      // the name tells the kind when it was left out: G1, JEN-2 generators, SEN-1 lines, the rest transformers
      const k: SourceKind = kind ?? (/^(g|jen|gen|dg)\s*-?\d/i.test(name) ? 'gen' : /^(sen|hat|line)/i.test(name) ? 'line' : 'tr');
      if (!name) name = `${k === 'gen' ? 'G' : k === 'tr' ? 'TR' : 'H'}${cur().sources.length + 1}`;
      cur().sources.push({
        kind: k, name, amps: amps || 250, sw: sw ?? (amps > 630 ? 'acb' : 'mccb'),
        ...(poles4 ? { poles4 } : {}), ...(meter ? { meter } : {}), ...(rest.length ? { note: rest.join(' · ').slice(0, 60) } : {}),
      });
      return;
    }
    // a feeder row
    let tag = '', amps = 0, poles: 1 | 3 | 4 = 3, rcd = false, busway = false, cableLen: number | undefined;
    const desc: string[] = [];
    cs.forEach((c, j) => {
      const fc = fold(c), a = ampsOf(c);
      if (j === 0 && /^[A-Z]{1,3}\d{1,3}$/.test(c)) tag = c;
      else if (a != null && !amps) amps = a;
      else if (/^[134]\s*p$/i.test(c)) poles = Number(c.trim()[0]) as 1 | 3 | 4;
      else if (/^\d+(?:[.,]\d+)?\s*m$/i.test(c)) cableLen = Number(c.replace(/\s*m$/i, '').replace(',', '.'));
      else if (/^(evet|kacak|rcd|30\s*ma|300\s*ma|fi)/.test(fc)) rcd = true;
      else if (/^(hayir|no|-)$/.test(fc)) return;
      else if (j > 1 && /^(bara kanali|busbar|busway|bara)$/.test(fc)) busway = true;
      else desc.push(c);
    });
    if (!amps) return void errors.push($t('Satır {0}: akım okunamadı: "{1}"', i + 1, line.slice(0, 40)));
    if (/(bara kanali|busbar|busway)/.test(fold(desc.join(' ')))) busway = true;
    cur().feeders.push({ ...(tag ? { tag } : {}), desc: desc.join(' ').slice(0, 60), amps, poles, ...(rcd ? { rcd } : {}), ...(busway ? { busway } : {}), ...(cableLen ? { cableLen } : {}) });
  });
  if (!sections.length) sections.push({ sources: [], feeders: [] });
  const spec: BoardSpec = {
    ...(title ? { title } : {}),
    sections,
    couplers: sections.slice(1).map((_, k) => couplers[k] ?? { amps: 0, sw: 'acb' as IncomerKind }),
    ...(sync ? { sync } : {}),
    ...(nHalf ? { nHalf } : {}),
  };
  spec.couplers!.forEach((c, k) => {
    if (!c.amps) errors.push($t('Bölüm {0} ile {1} arasında kuplaj satırı yok (ör. KUPLAJ 1600 A; ACB).', sections[k].name || sectionName(k), sections[k + 1].name || sectionName(k + 1)));
  });
  return { spec, errors: [...errors, ...checkBoard(spec).filter((e) => !e.includes('kuplaj sayısı') && !e.includes('Kuplaj sayısı'))] };
}

/** The text form of a board (for editing; from a single line or the AI). */
export function boardToText(spec: BoardSpec): string {
  const kindName: Record<SourceKind, string> = { tr: 'trafo', gen: 'jeneratör', line: 'hat' };
  const swName: Record<IncomerKind, string> = { acb: 'ACB', mccb: 'MCCB', lbs: 'yük ayırıcı' };
  const out: string[] = [];
  if (spec.title) out.push(`Pano: ${spec.title}`);
  if (spec.sync) out.push('Senkron');
  if (spec.nHalf) out.push('Nötr: yarım kesit');
  spec.sections.forEach((s, i) => {
    if (spec.sections.length > 1) out.push(`Bölüm ${s.name || sectionName(i)}`);
    for (const x of s.sources) out.push(['GİRİŞ ' + x.name, kindName[x.kind], `${x.amps} A`, swName[x.sw], x.poles4 ? '4P' : '3P', x.meter ? 'ölçüm' : '', x.note ?? ''].filter(Boolean).join('; '));
    for (const f of s.feeders)
      out.push([f.tag ?? '', f.desc || '-', `${f.amps} A`, `${f.poles}P`, f.rcd ? 'kaçak akım' : '', f.cableLen ? `${f.cableLen} m` : '', f.busway ? 'bara kanalı' : ''].filter((c, j) => c || j === 0).join('; '));
    if (s.comp?.length) out.push('KOMP ' + s.comp.join(', '));
    if (s.spd) out.push('PARAFUDR');
    const c = spec.couplers?.[i];
    if (c && i < spec.sections.length - 1) out.push(['KUPLAJ ' + `${c.amps} A`, swName[c.sw], c.poles4 ? '4P' : '3P'].join('; '));
  });
  return out.join('\n');
}

/* ---------------- board from a single-line page ---------------- */

/**
 * Reads a single-line page with one or more busbars: every horizontal line net that carries branches is a
 * busbar section (left to right); a breaker between two busbars is their coupler; branches going up from a
 * busbar are its sources, branches going down its feeders. Values come from the symbols (current, poles,
 * kind, busbar trunking, length, tag, description) and from texts written next to a source (TR1, G2, SEN-1).
 */
export function boardSpecFromSingleLine(page: Page, pages: Page[] = []): { spec: BoardSpec; warnings: string[] } | null {
  const warnings: string[] = [];
  // a single line over several pages: the pages linked by interruption points, read as one (side by side)
  const names = (p: Page) => p.syms.filter((s) => LIB[s.type]?.role === 'brk' && s.props.name).map((s) => s.props.name!);
  const linked = [page];
  for (let i = 0; i < linked.length; i++)
    for (const q of pages)
      if (q.kind === 'single' && !linked.includes(q) && names(q).some((n) => names(linked[i]).includes(n))) linked.push(q);
  const links: [Pt, Pt][] = [];
  if (linked.length > 1) {
    linked.sort((a, b) => pages.indexOf(a) - pages.indexOf(b));
    const off = (p: Page) => 1000 * linked.indexOf(p);
    const merged: Page = { ...page, syms: [], wires: [] };
    for (const p of linked) {
      merged.syms.push(...p.syms.map((s) => ({ ...s, x: s.x + off(p) })));
      merged.wires.push(...p.wires.map((w) => ({ ...w, pts: w.pts.map(([px, py]) => [px + off(p), py] as Pt) })));
    }
    const ends = merged.syms.filter((s) => LIB[s.type]?.role === 'brk');
    for (const a of ends) for (const b of ends) if (a !== b && a.props.name === b.props.name) links.push([[a.x, a.y], [b.x, b.y]]);
    page = merged;
  }
  const key = (x: number, y: number) => `${Math.round(x * 10)},${Math.round(y * 10)}`;
  const parent = new Map<string, string>();
  const find = (k: string): string => {
    if (!parent.has(k)) parent.set(k, k);
    let r = k;
    while (parent.get(r) !== r) r = parent.get(r)!;
    parent.set(k, r);
    return r;
  };
  const join = (a: string, b: string) => {
    const ra = find(a), rb = find(b);
    if (ra !== rb) parent.set(ra, rb);
  };
  const devs = page.syms.filter((s) => LIB[s.type]?.role === 'sl' && LIB[s.type].pins.length === 2);
  if (!devs.length) return null;
  const segs: [Pt, Pt][] = [];
  for (const w of page.wires) for (let i = 0; i < w.pts.length - 1; i++) segs.push([w.pts[i], w.pts[i + 1]]);
  for (const [a, b] of segs) join(key(...a), key(...b));
  for (const p of [...page.wires.flatMap((w) => w.pts), ...devs.flatMap((s) => pinsW(s))]) for (const [a, b] of segs) if (onSeg(p[0], p[1], a, b)) join(key(...p), key(...a));
  for (const [a, b] of links) join(key(...a), key(...b));
  const ends = (s: Sym) => {
    const [p, q] = pinsW(s);
    const [top, bottom] = p[1] <= q[1] ? [p, q] : [q, p];
    return { top: find(key(...top)), bottom: find(key(...bottom)), x: top[0], y: top[1] };
  };
  const E = new Map(devs.map((s) => [s.id, ends(s)]));
  // generators (one pin) at the end of a branch: drawn above the busbar or, as often, below it
  const gens = page.syms.filter((s) => s.type === 'sl_g').map((s) => ({ s, net: find(key(...pinsW(s)[0])) }));
  const genAt = (chain: Sym[], up: boolean) => {
    const last = E.get(chain[chain.length - 1].id)!;
    return gens.find((g) => g.net === (up ? last.top : last.bottom))?.s;
  };
  // busbars: nets with a long horizontal run and devices hanging on them
  const run = new Map<string, { len: number; x0: number; y: number }>();
  for (const [a, b] of segs)
    if (Math.abs(a[1] - b[1]) < 1e-6) {
      const r = find(key(...a)), cur = run.get(r) ?? { len: 0, x0: Infinity, y: a[1] };
      run.set(r, { len: cur.len + Math.abs(a[0] - b[0]), x0: Math.min(cur.x0, a[0], b[0]), y: cur.len ? cur.y : a[1] });
    }
  const touches = (net: string) => devs.filter((d) => E.get(d.id)!.top === net || E.get(d.id)!.bottom === net).length;
  const buses = [...run].filter(([net, r]) => r.len >= 30 && touches(net) >= 2).sort((a, b) => a[1].x0 - b[1].x0).map(([net]) => net);
  if (!buses.length) return null;
  const isBus = (net: string) => buses.includes(net);
  const amps = (s: Sym | undefined) => Number(String(s?.props.amps ?? '').replace(',', '.'));
  /** a branch from a busbar, symbol by symbol, until another busbar or the end */
  const branch = (start: Sym, up: boolean) => {
    const chain = [start], seen = new Set([start.id]);
    for (let cur = start; chain.length < 12; ) {
      const net = up ? E.get(cur.id)!.top : E.get(cur.id)!.bottom;
      if (isBus(net)) break;
      const next = devs.find((d) => !seen.has(d.id) && (up ? E.get(d.id)!.bottom : E.get(d.id)!.top) === net);
      if (!next) break;
      chain.push(next);
      seen.add(next.id);
      cur = next;
    }
    return chain;
  };
  const texts = page.syms.filter((s) => s.type === 'text' && s.props.text);
  /** a source's name: a transformer's tag, a text right above its branch, else the breaker's description or tag */
  const nameOf = (chain: Sym[], brk: Sym | undefined, gen?: Sym) => {
    const tr = chain.find((d) => d.type === 'sl_tr');
    if (tr?.tag) return tr.tag;
    if (gen?.tag) return gen.tag;
    const top = chain[chain.length - 1], e = E.get(top.id)!;
    const near = texts
      .filter((t) => t.y < e.y && t.y > e.y - 40 && Math.abs(t.x - e.x) < 20)
      .sort((a, b) => Math.abs(a.x - e.x) + (e.y - a.y) - (Math.abs(b.x - e.x) + (e.y - b.y)))[0];
    const word = near ? String(near.props.text).match(/\b(TR\s*-?\s*\d+|G\s*-?\s*\d+|JEN\s*-?\s*\d+|SEN\s*-?\s*\d+|[A-ZÇĞİÖŞÜ]{1,4}-?\d+)\b/i)?.[1] : undefined;
    return (word || brk?.desc || brk?.tag || '').replace(/\s+/g, '').slice(0, 20);
  };
  const kindOf = (chain: Sym[], brk: Sym | undefined, name: string, gen?: Sym): SourceKind => {
    const k = brk?.props.source;
    if (k === 'tr' || k === 'gen' || k === 'line') return k;
    if (gen) return 'gen';
    if (chain.some((d) => d.type === 'sl_tr') || /^TR/i.test(name)) return 'tr';
    if (/^(G|JEN|DG)\d/i.test(name)) return 'gen';
    return /^(SEN|HAT|H)\d?/i.test(name) ? 'line' : 'tr';
  };
  const swOfSym = (s: Sym | undefined, a: number): IncomerKind => {
    const k = s?.props.swkind;
    return k === 'acb' || k === 'mccb' || k === 'lbs' ? k : a > 630 ? 'acb' : 'mccb';
  };
  const sections: SectionSpec[] = buses.map(() => ({ sources: [], feeders: [] }));
  const couplers: NonNullable<BoardSpec['couplers']> = [];
  for (const d of devs) {
    const e = E.get(d.id)!;
    if (d.type !== 'sl_cb' || !isBus(e.top) || !isBus(e.bottom) || e.top === e.bottom) continue;
    // a coupler: both ends on busbars (its loop is drawn through wires, so follow them)
    const i = buses.indexOf(e.top), j = buses.indexOf(e.bottom), lo = Math.min(i, j);
    if (Math.abs(i - j) !== 1) warnings.push($t('{0}: kuplaj komşu olmayan iki barayı bağlıyor.', d.tag || 'Q'));
    const a = amps(d) || 630;
    couplers[lo] = { amps: a, sw: swOfSym(d, a), ...(d.props.poles === '4' ? { poles4: true } : {}) };
  }
  buses.forEach((bus, bi) => {
    // sources: branches going up from this busbar, and branches going down to a generator or transformer
    const found: { x: number; src: SourceSpec }[] = [];
    const source = (head: Sym, chain: Sym[], gen: Sym | undefined) => {
      const brk = chain.find((d) => d.type === 'sl_cb');
      const a = amps(brk);
      const name = nameOf(chain, brk, gen) || `${bi + 1}.${found.length + 1}`;
      if (!(a > 0)) warnings.push($t('{0}: giriş şalterinin akımı girilmemiş, 250 A alındı.', name));
      found.push({ x: E.get(head.id)!.x, src: { kind: kindOf(chain, brk, name, gen), name, amps: a > 0 ? a : 250, sw: swOfSym(brk, a), ...(brk?.props.poles === '4' ? { poles4: true } : {}) } });
    };
    devs
      .filter((d) => E.get(d.id)!.bottom === bus && !isBus(E.get(d.id)!.top))
      .forEach((head) => {
        const chain = branch(head, true);
        source(head, chain, genAt(chain, true));
      });
    // feeders: branches going down, left to right
    devs
      .filter((d) => E.get(d.id)!.top === bus && !isBus(E.get(d.id)!.bottom))
      .sort((a, b) => E.get(a.id)!.x - E.get(b.id)!.x)
      .forEach((head) => {
        const chain = branch(head, false);
        const brk = chain.find((d) => d.type === 'sl_cb'), rcd = chain.find((d) => d.type === 'sl_rcd'), gen = genAt(chain, false);
        if (gen || chain.some((d) => d.type === 'sl_tr') || brk?.props.source) return source(head, chain, gen);
        let a = amps(brk) || amps(rcd);
        if (!(a > 0)) {
          warnings.push($t('{0}: akım girilmemiş, 16 A alındı.', brk?.tag || head.tag || $t('Çıkış')));
          a = 16;
        }
        const len = Number(String(brk?.props.length ?? '').replace(',', '.'));
        const poles = brk?.props.poles === '1' ? 1 : brk?.props.poles === '4' ? 4 : 3;
        sections[bi].feeders.push({
          ...(brk?.tag ? { tag: brk.tag } : {}),
          desc: brk?.desc || rcd?.desc || '',
          amps: Math.min(a, MAX_BOARD_FEEDER),
          poles,
          ...(rcd ? { rcd: true } : {}),
          ...(brk?.props.busway ? { busway: true } : {}),
          ...(len > 0 ? { cableLen: len } : {}),
        });
      });
    sections[bi].sources = found.sort((p, q) => p.x - q.x).map((f) => f.src);
  });
  for (let k = 0; k < buses.length - 1; k++)
    if (!couplers[k]) {
      warnings.push($t('Bara {0} ile {1} arasında kuplaj bulunamadı; ayrı panolar olarak okundu, kuplajı eklemek için metni düzenleyin.', sectionName(k), sectionName(k + 1)));
      couplers[k] = { amps: 0, sw: 'acb' };
    }
  if (sections.every((s) => !s.sources.length)) warnings.push($t('Tek hatta baranın üstünde bir giriş şalteri bulunamadı.'));
  const sync = sections.length === 1 && sections[0].sources.length > 2 && sections[0].sources.every((s) => s.kind === 'gen');
  return { spec: { sections, couplers, ...(sync ? { sync } : {}) }, warnings };
}
